/**
 * Shared searchable-select components used by the Deal form and the Contact
 * form (and anywhere else the app needs to pick a company or contact from a
 * potentially large list).
 *
 * Design notes:
 * - Alphabetical sort — a searchable list still benefits from a deterministic
 *   scan order when you don't remember the exact spelling.
 * - "Clear selection" affordance appears only when a value is picked so the
 *   dropdown starts uncluttered.
 * - ContactCombobox is STRICT about `companyId`: when provided, only contacts
 *   whose `company_id === companyId` are shown. No "orphan" contacts leak
 *   into a company-scoped picker — that was the earlier bug.
 */
import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

/* -------------------- Company -------------------- */
export function CompanyCombobox({
  companies, value, onChange,
  placeholder = "Select company…",
  searchPlaceholder = "Search companies…",
  testId = "combobox-company",
  allowClear = false,
}) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(
    () => [...companies].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
    ),
    [companies]
  );
  const selected = sorted.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="h-9 border border-input rounded-md px-2.5 text-sm bg-background flex-1 min-w-0 flex items-center justify-between gap-2 hover:bg-accent/40 transition-colors"
        >
          <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>
            {selected?.name || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[280px]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No company found.</CommandEmpty>
            {allowClear && value && (
              <CommandGroup>
                <CommandItem
                  value="__clear__ none"
                  onSelect={() => { onChange(""); setOpen(false); }}
                  data-testid={`${testId}-option-clear`}
                >
                  <span className="text-muted-foreground italic">— Clear selection —</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {sorted.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.name}
                  data-testid={`${testId}-option-${c.id}`}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/* -------------------- Contact -------------------- */
export function ContactCombobox({
  contacts, companyId, value, onChange,
  placeholder = "Select contact…",
  searchPlaceholder = "Search contacts — name, email, mobile…",
  testId = "combobox-contact",
}) {
  const [open, setOpen] = useState(false);
  // STRICT scoping: when a company is selected, ONLY that company's contacts
  // are visible. Orphan contacts (no company_id) are never shown in a
  // company-scoped list — that was the previous leaky behavior.
  const sorted = useMemo(() => {
    const scoped = companyId ? contacts.filter((c) => c.company_id === companyId) : contacts;
    return [...scoped].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
    );
  }, [contacts, companyId]);
  const selected = contacts.find((c) => c.id === value);
  const label = (c) => [c.name, c.email, c.mobile, c.phone].filter(Boolean).join(" ").trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="h-9 border border-input rounded-md px-2.5 text-sm bg-background flex-1 min-w-0 flex items-center justify-between gap-2 hover:bg-accent/40 transition-colors"
        >
          <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>
            {selected?.name || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>
              {companyId ? "No contacts for this company." : "No contact found."}
            </CommandEmpty>
            {value && (
              <CommandGroup>
                <CommandItem
                  value="__clear__ none"
                  onSelect={() => { onChange(""); setOpen(false); }}
                  data-testid={`${testId}-option-clear`}
                >
                  <span className="text-muted-foreground italic">— Clear selection —</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {sorted.map((c) => (
                <CommandItem
                  key={c.id}
                  value={label(c)}
                  data-testid={`${testId}-option-${c.id}`}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{c.name}</div>
                    {(c.email || c.mobile || c.phone) && (
                      <div className="truncate text-xs text-muted-foreground">
                        {c.email || c.mobile || c.phone}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
